<template>
    <div
        ref="track"
        class="relative w-full h-16 user-select-none"
        :class="[`bg-${props.color}-400`, { 'cursor-pointer': !isFilled }]"
        @click="!isFilled ? create($event) : null"
        @pointerup="dragEnd"
        @pointerleave="dragEnd"
    >
        <div class="absolute px-2 text-xs font-semibold text-white pointer-events-none left-1 bottom-2">
            {{ start }}
        </div>
        <div class="absolute px-2 text-xs font-semibold text-white pointer-events-none right-1 bottom-2">
            {{ end }}
        </div>
        <div
            :style="{ marginLeft: startPosition, width: width }"
            class="relative z-10 flex items-stretch h-full rounded group"
        >
            <div
                class="absolute inset-x-0 flex items-center text-xs -translate-y-full bg-gray-800 rounded-t opacity-0 group-hover:opacity-100 h-1/2 text-gray-50"
            >
                <div class="flex justify-end flex-auto">
                    {{ from }}
                </div>
                <div class="grow-0">-</div>
                <div class="flex justify-start flex-auto">
                    {{ to }}
                </div>
            </div>
            <div
                class="flex items-center grow-0 bg-blue-600 border-blue-600 rounded-l text-blue-50 cursor-ew-resize border-y-2 hover:border-blue-400 hover:bg-blue-500 -ml-[25px]"
                @pointerdown="dragStart('start', $event)"
            >
                <svg width="25px" height="20px" version="1.1" viewBox="0 0 20 20" x="0px" y="0px" fill="currentColor">
                    <g>
                        <path
                            d="M12.5 11a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM12.5 1a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM7.5 1a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM6 7.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM12.5 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM6 12.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM11 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM7.5 16a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"
                        ></path>
                    </g>
                </svg>
            </div>
            <div
                class="flex-auto border-blue-600 bg-blue-600/20 cursor-grab border-y-2 hover:border-blue-400"
                @pointerdown="dragStart('body', $event)"
            ></div>
            <div
                class="flex items-center grow-0 bg-blue-600 border-blue-600 rounded-r text-blue-50 cursor-ew-resize border-y-2 hover:border-blue-400 hover:bg-blue-500 -mr-[25px]"
                @pointerdown="dragStart('end', $event)"
            >
                <svg width="25px" height="20px" version="1.1" viewBox="0 0 20 20" x="0px" y="0px" fill="currentColor">
                    <g>
                        <path
                            d="M12.5 11a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM12.5 1a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM7.5 1a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM6 7.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM12.5 6a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM6 12.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM11 17.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM7.5 16a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"
                        ></path>
                    </g>
                </svg>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, computed, Ref, ref, watch } from 'vue';

const emit = defineEmits<{
    (event: 'update', { from, to }): void;
}>();

interface Props {
    from?: string;
    to?: string;
    start?: string;
    end?: string;
    segment?: string;
    color?: string;
}

const props = withDefaults(defineProps<Props>(), {
    from: '8:00', // Selection start
    to: '11:00', // Selection end

    start: '7:00', // Track starts
    end: '22:00', // Track end

    segment: '00:30', // Segment size

    color: 'brand',
});

const track: Ref<HTMLElement | null> = ref(null);
const from = ref(props.from ?? '8:00');
const to = ref(props.to ?? '11:00');
const start = ref(props.start ?? '7:00');
const end = ref(props.end ?? '22:00');
const segment = ref(props.segment ?? '00:30');
const bounding = ref({
    x: 0,
    y: 0,
});

const isFilled = computed((): boolean => from.value && to.value);

const totalWidth = computed((): number => track.value?.clientWidth ?? 0);

const minutesFromString = (time: string): number => {
    const [hours, minutes] = time.split(':');
    return parseInt(hours) * 60 + parseInt(minutes);
};

const segmentSize = computed((): number => minutesFromString(segment.value)); // Number of minutes in one segment
const minutesStart = computed((): number => (start.value ? minutesFromString(start.value) : 0)); // Number of minutes at start
const minutesEnd = computed((): number => (end.value ? minutesFromString(end.value) : 0)); // Number of minutes at end
const minutesFrom = computed((): number => (from.value ? minutesFromString(from.value) : 0)); // Number of minutes from
const minutesTo = computed((): number => (to.value ? minutesFromString(to.value) : 0)); // Number of minutes to
const minutesTotal = computed((): number => minutesEnd.value - minutesStart.value); // Total number of minutes
const totalSegments = computed((): number => minutesTotal.value / segmentSize.value); // Number of segments on track
const segmentWidth = computed((): number => totalWidth.value / totalSegments.value); // Width of one segment in pixels
const minutesFromStart = computed((): number => minutesFrom.value - minutesStart.value); // Number of minutes from start
const minutesDuration = computed((): number => minutesTo.value - minutesFrom.value); // Number of minutes duration

const startPosition = computed((): string => {
    const position = (minutesFromStart.value / minutesTotal.value) * 100;
    return `${position}%`;
});

const width = computed((): string => {
    const width = (minutesDuration.value / minutesTotal.value) * 100;
    return `${width}%`;
});

const dragging: Ref<string | null> = ref(null);
const originX: Ref<number> = ref(0);
const originFrom: Ref<string | null> = ref(null);
const originTo: Ref<string | null> = ref(null);
const originMinutesFrom = computed((): number => (originFrom.value ? minutesFromString(originFrom.value) : 0));
const originMinutesTo = computed((): number => (originTo.value ? minutesFromString(originTo.value) : 0));

const dragStart = (handle: string, event: PointerEvent) => {
    dragging.value = handle;
    originX.value = event.clientX;
    originFrom.value = from.value;
    originTo.value = to.value;
    track.value?.addEventListener('mousemove', dragMove);
};

const dragEnd = () => {
    dragging.value = null;
    track.value?.removeEventListener('mousemove', dragMove);
};

const dragValue = (coordinate: number) => {
    const position = (coordinate - bounding.value.x) / totalWidth.value;
    const minutes = Math.round(position * minutesTotal.value);
    const minutesRounded = Math.round(minutes / segmentSize.value) * segmentSize.value + minutesStart.value;

    if (minutesRounded < minutesStart.value) {
        return minutesStart.value;
    } else if (minutesRounded > minutesEnd.value) {
        return minutesEnd.value;
    }

    if (dragging.value === 'start') {
        // Moving with start handle
        if (minutesRounded >= minutesTo.value) {
            from.value = to.value;
        } else {
            from.value = `${Math.floor(minutesRounded / 60)}:${getMinutesString(minutesRounded % 60)}`;
        }
    } else if (dragging.value === 'end') {
        // Moving with end handle
        if (minutesRounded <= minutesFrom.value) {
            to.value = from.value;
        } else {
            to.value = `${Math.floor(minutesRounded / 60)}:${getMinutesString(minutesRounded % 60)}`;
        }
    } else if (dragging.value === 'body') {
        // Moving with body
        const shifted = Math.round((coordinate - originX.value) / segmentWidth.value);
        const minutesShifted = (minutesTotal.value / totalSegments.value) * shifted;
        const newStart = originMinutesFrom.value + minutesShifted;
        const newEnd = originMinutesTo.value + minutesShifted;

        if (newStart < minutesStart.value || newEnd > minutesEnd.value) {
            return;
        }

        from.value = `${Math.round(newStart / 60)}:${getMinutesString(newStart % 60)}`;
        to.value = `${Math.round(newEnd / 60)}:${getMinutesString(newEnd % 60)}`;
    }
};

const dragMove = (event) => {
    dragValue(event.clientX);
};

const getMinutesString = (minutes: number): string => {
    return minutes < 10 ? `0${minutes}` : `${minutes}`;
};

const create = (event) => {
    const coordinate = event.clientX;
    const position = (coordinate - bounding.value.x) / totalWidth.value;
    const minutes = Math.round(position * minutesTotal.value);
    const minutesRounded = Math.round(minutes / segmentSize.value) * segmentSize.value + minutesStart.value;
    const minutesEnd = minutesRounded + 120;

    from.value = `${Math.round(minutesRounded / 60)}:${getMinutesString(minutesRounded % 60)}`;
    to.value = `${Math.round(minutesEnd / 60)}:${getMinutesString(minutesEnd % 60)}`;
};

watch([from, to], () => {
    emit('update', {
        from: from.value,
        to: to.value,
    });
});

onMounted(() => {
    bounding.value = track.value.getBoundingClientRect();
});
</script>
